import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { QuantileCells } from '@/pages/ibama/OverviewPage';
import {
  Figure,
  LoadMore,
  NameLink,
  PersonScopeCard,
  TABLE_REGION,
  TD,
  TD_MUTED,
  TD_NUM,
  TH,
  TH_NUM,
  THEAD,
  caseHref,
  partyHref,
  useMoney,
} from '@/pages/cvm/shared';
import { cvmLedgerParams, fetchCvmLedger, fetchCvmSanctions } from '@/services/cvm';
import type { CvmLedgerRow, CvmProvisionRow, CvmYearShares } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * Sanctions as the extrato attributes them: one row per accused per item.
 *
 * The top half is the corpus: how many rows the parser attributed to a named
 * accused, the fine ladder per violated provision (a ladder only where the
 * provision carries enough fines; below the floor the row prints its count and
 * no quantile), and the sanction type mix by year. The bottom half is the
 * ledger itself, filtered, each row with the clause it was cut from. Nothing
 * here totals a party across cases; that refusal is a card, not a footnote.
 */
export function CvmSanctionsPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const [seed] = useSearchParams();
  const [sanctionType, setSanctionType] = useState(seed.get('sanction_type') ?? '');
  const [provision, setProvision] = useState(seed.get('provision') ?? '');
  const [year, setYear] = useState(seed.get('year') ?? '');
  const [party, setParty] = useState(seed.get('party') ?? '');
  const [attributedOnly, setAttributedOnly] = useState(seed.get('attributed_only') === 'true');
  const filters = {
    sanction_type: sanctionType || null,
    provision: provision || null,
    year: year ? Number(year) : null,
    party: party || null,
    attributed_only: attributedOnly,
  };

  const summary = useQuery({
    queryKey: ['cvm', 'sanctions'],
    queryFn: fetchCvmSanctions,
  });
  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['cvm', 'sanctions', 'ledger', filters],
    queryFn: ({ pageParam }) => fetchCvmLedger({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const ledger = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);
  // The ladder cells are narrow: R$ 10 mil reads where R$ 10.000,00 overflows.
  const compact = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  });
  const data = summary.data;
  const types = data?.type_by_year.value
    ? Array.from(
        new Set(data.type_by_year.value.flatMap((row) => row.shares.map((s) => s.code))),
      ).sort()
    : [];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:sanctions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:sanctions.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:sanctions.explainer" />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {summary.isPending ? (
        <SkeletonLoader rows={6} />
      ) : summary.error ? (
        <ErrorMessage message={summary.error.message} onRetry={() => void summary.refetch()} />
      ) : data ? (
        <>
          <section className="rounded-lg border bg-card p-4" data-attribution>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('cvm:sanctions.attribution.title')}
            </h2>
            <Answer answer={data.attribution} help={t('cvm:sanctions.attribution.help')}>
              {(a) => (
                <>
                  <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <Figure
                      label={t('cvm:sanctions.attribution.rows')}
                      value={number.format(a.rows)}
                    />
                    <Figure
                      label={t('cvm:sanctions.attribution.attributed')}
                      value={number.format(a.attributed)}
                    />
                    <Figure
                      label={t('cvm:sanctions.attribution.withAmount')}
                      value={number.format(a.with_amount)}
                    />
                    <Figure
                      label={t('cvm:sanctions.attribution.withProvision')}
                      value={number.format(a.with_provision)}
                    />
                  </dl>
                  <dl className="mt-2 grid grid-cols-3 gap-2">
                    <Figure
                      label={t('cvm:sanctions.attribution.individualEach')}
                      value={number.format(a.individual_each)}
                    />
                    <Figure
                      label={t('cvm:sanctions.attribution.clause')}
                      value={number.format(a.clause)}
                    />
                    <Figure
                      label={t('cvm:sanctions.attribution.verified')}
                      value={number.format(a.evidence_verified)}
                    />
                  </dl>
                </>
              )}
            </Answer>
          </section>

          <section className="rounded-lg border bg-card p-4" data-provisions>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('cvm:sanctions.provisions.title')}
            </h2>
            <Answer answer={data.provisions} help={t('cvm:sanctions.provisions.help')}>
              {(rows: CvmProvisionRow[]) => (
                <div className="max-h-96 overflow-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className={THEAD}>
                      <tr>
                        <th className={TH}>{t('cvm:sanctions.provisions.provision')}</th>
                        <th className={TH_NUM}>{t('cvm:sanctions.provisions.decisions')}</th>
                        <th className={TH_NUM}>{t('cvm:sanctions.provisions.fines')}</th>
                        <th className={TH}>{t('cvm:sanctions.provisions.ladder')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.provision} className="border-t align-top">
                          <td className={TD}>
                            <button
                              type="button"
                              className="text-left text-primary underline-offset-2 hover:underline"
                              onClick={() => setProvision(row.provision)}
                            >
                              {row.provision}
                            </button>
                          </td>
                          <td className={TD_NUM}>{number.format(row.decisions)}</td>
                          <td className={TD_NUM}>{number.format(row.fines)}</td>
                          <td className="px-3 py-2">
                            {row.summary ? (
                              <QuantileCells
                                summary={row.summary}
                                format={(v) => compact.format(v)}
                              />
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {t('cvm:sanctions.provisions.belowFloor', {
                                  floor: row.refused_minimum ?? '—',
                                })}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Answer>
          </section>

          <section className="rounded-lg border bg-card p-4" data-type-by-year>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('cvm:sanctions.typeByYear.title')}
            </h2>
            <Answer answer={data.type_by_year} help={t('cvm:sanctions.typeByYear.help')}>
              {(rows: CvmYearShares[]) => (
                <div className="max-h-96 overflow-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className={THEAD}>
                      <tr>
                        <th className={TH}>{t('cvm:sanctions.typeByYear.year')}</th>
                        {types.map((code) => (
                          <th key={code} className={TH_NUM}>
                            {t(`cvm:sanctionTypes.${code}`, {
                              defaultValue: code,
                            })}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.year} className="border-t">
                          <td className={TD}>{row.year}</td>
                          {types.map((code) => {
                            const share = row.shares.find((s) => s.code === code);
                            return (
                              <td key={code} className={TD_NUM}>
                                {share ? number.format(share.count) : '—'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Answer>
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            <RefusalCard title={t('cvm:sanctions.partyTotals.title')} answer={data.party_totals} />
            <PersonScopeCard answer={data.person_scope} />
          </div>
        </>
      ) : null}

      <section className="space-y-3" data-ledger>
        <h2 className="text-sm font-semibold text-foreground">{t('cvm:sanctions.ledger.title')}</h2>
        <form
          className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
          onSubmit={(event) => event.preventDefault()}
        >
          <div className="space-y-1">
            <label htmlFor="cvm-ledger-type" className="block text-xs font-medium text-foreground">
              {t('cvm:sanctions.ledger.filters.type')}
            </label>
            <select
              id="cvm-ledger-type"
              value={sanctionType}
              onChange={(event) => setSanctionType(event.target.value)}
              className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
            >
              <option value="">{t('cvm:sanctions.ledger.filters.any')}</option>
              {types.map((code) => (
                <option key={code} value={code}>
                  {t(`cvm:sanctionTypes.${code}`, { defaultValue: code })}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[14rem] flex-1 space-y-1">
            <label
              htmlFor="cvm-ledger-provision"
              className="block text-xs font-medium text-foreground"
            >
              {t('cvm:sanctions.ledger.filters.provision')}
            </label>
            <input
              id="cvm-ledger-provision"
              value={provision}
              onChange={(event) => setProvision(event.target.value)}
              placeholder={t('cvm:sanctions.ledger.filters.provisionPlaceholder')}
              className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="cvm-ledger-year" className="block text-xs font-medium text-foreground">
              {t('cvm:sanctions.ledger.filters.year')}
            </label>
            <input
              id="cvm-ledger-year"
              inputMode="numeric"
              value={year}
              onChange={(event) => setYear(event.target.value.replace(/\D/g, '').slice(0, 4))}
              className="w-24 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              checked={attributedOnly}
              onChange={(event) => setAttributedOnly(event.target.checked)}
            />
            {t('cvm:sanctions.ledger.filters.attributedOnly')}
          </label>
          {party ? (
            <button
              type="button"
              onClick={() => setParty('')}
              className="rounded border px-2 py-1 text-xs text-foreground"
            >
              {t('cvm:sanctions.ledger.filters.clearParty', { party })}
            </button>
          ) : null}
          <div className="ml-auto">
            <ExportButton
              path={`/api/v1/cvm/export/sanctions/ledger?${cvmLedgerParams(filters).toString()}`}
            />
          </div>
        </form>

        {isPending ? (
          <SkeletonLoader rows={8} />
        ) : error ? (
          <ErrorMessage message={error.message} onRetry={() => void refetch()} />
        ) : !ledger ? (
          <SkeletonLoader rows={8} />
        ) : ledger.total === 0 ? (
          <EmptyState hint={t('cvm:list.empty')} />
        ) : (
          <>
            <p
              className="text-xs text-muted-foreground"
              aria-live="polite"
              data-total={ledger.total}
              data-stale={isPlaceholderData ? 'true' : 'false'}
            >
              {t('cvm:list.showing', {
                loaded: number.format(items.length),
                total: number.format(ledger.total),
              })}
            </p>
            <div
              ref={scrollRef}
              className={TABLE_REGION}
              role="region"
              aria-label={t('cvm:sanctions.ledger.tableLabel')}
            >
              <table className="w-full text-sm">
                <thead className={THEAD}>
                  <tr>
                    <th className={TH}>{t('cvm:sanctions.ledger.columns.process')}</th>
                    <th className={TH}>{t('cvm:sanctions.ledger.columns.party')}</th>
                    <th className={TH}>{t('cvm:sanctions.ledger.columns.type')}</th>
                    <th className={TH_NUM}>{t('cvm:sanctions.ledger.columns.amount')}</th>
                    <th className={TH}>{t('cvm:sanctions.ledger.columns.provision')}</th>
                    <th className={TH}>{t('cvm:sanctions.ledger.columns.evidence')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, index) => (
                    <LedgerRow key={`${row.case_key}-${row.item_seq}-${index}`} row={row} />
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} aria-hidden className="h-px" />
              <LoadMore
                hasNextPage={Boolean(hasNextPage)}
                isFetchingNextPage={isFetchingNextPage}
                loadMore={loadMore}
                total={ledger.total}
                pageSize={PAGE_SIZE}
              />
            </div>
          </>
        )}
      </section>

      <FieldGuide i18nKey="cvm:sanctions.guide" />
    </div>
  );
}

function LedgerRow({ row }: { row: CvmLedgerRow }) {
  const { t } = useTranslation(['cvm']);
  const money = useMoney();
  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className={TD}>
        <Link
          to={caseHref(row.case_key)}
          className="text-primary underline-offset-2 hover:underline"
        >
          {row.process_number}
        </Link>
        <div className="text-xs text-muted-foreground">
          {row.judgment_date ?? '—'} · {t('cvm:case.walk.item', { seq: row.item_seq })}
        </div>
      </td>
      <td className={TD}>
        <NameLink name={row.party ?? row.party_name_raw} href={partyHref(row.party_slug)} />
        {row.entity_type ? (
          <div className="text-xs text-muted-foreground">
            {t(`cvm:entityTypes.${row.entity_type}`, {
              defaultValue: row.entity_type,
            })}
          </div>
        ) : null}
      </td>
      <td className={TD}>
        {t(`cvm:sanctionTypes.${row.sanction_type}`, {
          defaultValue: row.sanction_type,
        })}
        {row.duration_raw ? (
          <div className="text-xs text-muted-foreground">{row.duration_raw}</div>
        ) : null}
      </td>
      <td className={TD_NUM}>
        {money(row.amount_brl)}
        {row.amount_attribution ? (
          <div className="text-xs font-normal text-muted-foreground">
            {t(`cvm:attributions.${row.amount_attribution}`, {
              defaultValue: row.amount_attribution,
            })}
          </div>
        ) : null}
      </td>
      <td className={TD_MUTED}>
        <div className="text-foreground">{row.provision ?? '—'}</div>
        {row.provisions.length > 1 ? <div>{row.provisions.join('; ')}</div> : null}
      </td>
      <td className={TD_MUTED}>
        <span title={row.evidence_span}>
          {row.evidence_span.length > 140
            ? `${row.evidence_span.slice(0, 140)}…`
            : row.evidence_span}
        </span>
        {!row.evidence_verified ? (
          <div className="text-amber-600">{t('cvm:case.sanctions.unverified')}</div>
        ) : null}
      </td>
    </tr>
  );
}
